/*
 * The ten analyses as the scene shows them: where the camera stands for each, which points
 * light up, what is drawn over them and what the readout says. In station order, so the
 * camera drives down the corridor from one analysis to the next.
 */
import { ANALYSES } from "../../lib/analyses.js";
import {
  FT,
  KIND,
  LANE,
  SHOULDER,
  SITE,
  STANDARDS,
  STRIDE,
  embankment,
  heightAt,
  toWorld,
} from "./world.js";

export const ORDER = [
  "ssd",
  "psd",
  "isd",
  "signs",
  "flat-spots",
  "curves",
  "glare",
  "guardrail",
  "embankment",
  "clear-zone",
];

/** Highlight colours, by index; 0 means "not part of this analysis" (drawn dimmed). */
export const COLORS = [
  null,
  [70, 214, 150], // 1 green: meets the criterion
  [255, 140, 20], // 2 orange: short of it
  [255, 86, 72], // 3 red: fails
  [250, 204, 21], // 4 yellow
  [34, 211, 238], // 5 cyan
  [192, 132, 252], // 6 purple
  [96, 165, 250], // 7 blue
  [163, 230, 53], // 8 lime
  [244, 114, 182], // 9 pink
  [251, 176, 60], // 10 amber
  [240, 244, 248], // 11 white
];
const C = {
  NONE: 0,
  GREEN: 1,
  ORANGE: 2,
  RED: 3,
  YELLOW: 4,
  CYAN: 5,
  PURPLE: 6,
  BLUE: 7,
  LIME: 8,
  PINK: 9,
  AMBER: 10,
  WHITE: 11,
};
const css = (i, a = 1) => `rgba(${COLORS[i].join(",")},${a})`;

/**
 * Camera shots: where the camera stands and what it looks at, as station, offset and
 * height above the road's profile at that station.
 */
export const SHOTS = {
  // From the side, square to the road: the driver, the line hitting the hump and the object
  // 495 ft out past it all fit across the frame.
  ssd: { cam: { s: 162, o: 185, h: 11 }, look: { s: 162, o: 0, h: 3 } },
  psd: { cam: { s: 262, o: -4, h: 13 }, look: { s: 430, o: 0, h: 0 } },
  isd: { cam: { s: 418, o: 46, h: 34 }, look: { s: 466, o: 0, h: 0 } },
  signs: { cam: { s: 478, o: 2.2, h: 2.1 }, look: { s: 590, o: 5, h: 2.2 } },
  "flat-spots": { cam: { s: 488, o: -6, h: 10 }, look: { s: 532, o: 2, h: 0 } },
  // Beyond the curve's centre, looking back at the arc: the centre, both radii and the arc in frame.
  curves: { cam: { s: 640, o: -330, h: 330 }, look: { s: 640, o: -95, h: 0 } },
  glare: { cam: { s: 792, o: 1.8, h: 1.3 }, look: { s: 900, o: 1.8, h: 2.6 } },
  guardrail: { cam: { s: 942, o: 34, h: 13 }, look: { s: 1028, o: 8, h: -2 } },
  embankment: { cam: { s: 1008, o: 62, h: 8 }, look: { s: 1030, o: 6, h: -2 } },
  "clear-zone": { cam: { s: 1104, o: 3, h: 4.2 }, look: { s: 1212, o: 7, h: 0 } },
};

/**
 * How an analysis's highlight spreads once its step is on screen: out from station `from`,
 * at `speed` m/s. Analyses without one light up at once.
 */
export function spread(key, r) {
  switch (key) {
    case "ssd":
      return { from: r.ssd.eye.s, speed: 150 };
    case "psd":
      return { from: SITE.passing[0], speed: 160 };
    case "curves":
      return { from: SITE.curve.spiralIn[0], speed: 220 };
    case "glare":
      return { from: r.glr.from, speed: 260 };
    case "guardrail":
      return { from: SITE.embankment[0], speed: 120 };
    case "clear-zone":
      return { from: SITE.clearZone[0], speed: 120 };
    default:
      return null;
  }
}

const onRoad = (k) => k <= KIND.CENTRE_LINE;

/** Per-point highlight for an analysis: (points, index) -> colour index. */
export function painter(key, r) {
  switch (key) {
    case "ssd": {
      const e = r.ssd.eye.s;
      return (P, i) => {
        if (!onRoad(P[i + 4])) return C.NONE;
        const s = P[i + 5];
        const o = P[i + 6];
        if (o < -0.2 || o > LANE + 0.2 || s < e) return C.NONE;
        if (s <= e + r.ssd.available) return C.GREEN;
        return s <= e + r.ssd.required ? C.ORANGE : C.NONE;
      };
    }
    case "psd": {
      const [z0, z1] = SITE.passing;
      return (P, i) => {
        if (P[i + 4] !== KIND.CENTRE_LINE) return C.NONE;
        const s = P[i + 5];
        if (s < z0 || s > z1) return C.NONE;
        return r.psd.isShort(s) ? C.RED : C.GREEN;
      };
    }
    case "isd": {
      const tri = r.isd.triangles;
      const brushIds = r.isd.brushIds;
      return (P, i) => {
        const kind = P[i + 4];
        if (kind === KIND.CANOPY || kind === KIND.TRUNK) {
          return brushIds.has(P[i + 7]) ? C.RED : C.NONE;
        }
        const x = P[i];
        const z = P[i + 2];
        if (inTriangle(tri.leftClear, x, z) || inTriangle(tri.rightClear, x, z)) return C.GREEN;
        if (inTriangle(tri.rightBlocked, x, z)) return C.ORANGE;
        return C.NONE;
      };
    }
    case "signs":
      return (P, i) => {
        const kind = P[i + 4];
        return kind === KIND.SIGN_FACE || kind === KIND.SIGN_POST ? C.PINK : C.NONE;
      };
    case "flat-spots":
      return (P, i) => {
        if (!onRoad(P[i + 4])) return C.NONE;
        const s = P[i + 5];
        if (P[i + 6] < -0.1 || s < 490 || s > 790) return C.NONE;
        if (r.fsa.isFlat(s)) return C.CYAN;
        return s > 500 && s < 780 ? C.BLUE : C.NONE;
      };
    case "curves": {
      const [a] = SITE.curve.spiralIn;
      const [, b] = SITE.curve.spiralOut;
      return (P, i) => {
        if (!onRoad(P[i + 4])) return C.NONE;
        const s = P[i + 5];
        return s >= a && s <= b ? C.PURPLE : C.NONE;
      };
    }
    case "glare":
      return (P, i) => {
        if (!onRoad(P[i + 4])) return C.NONE;
        const s = P[i + 5];
        return s >= r.glr.from && s <= r.glr.to ? C.YELLOW : C.NONE;
      };
    case "guardrail": {
      const [r0, r1] = SITE.guardrail;
      return (P, i) => {
        const kind = P[i + 4];
        if (kind === KIND.GUARDRAIL) return C.WHITE;
        if (kind !== KIND.GROUND) return C.NONE;
        const s = P[i + 5];
        const fill = embankment(s);
        const o = P[i + 6];
        if (fill < 0.3 || o < SHOULDER || o > SHOULDER + 2 * fill + 0.6) return C.NONE;
        if (!r.grw.isWarranted(s)) return C.GREEN;
        return s >= r0 && s <= r1 ? C.AMBER : C.RED;
      };
    }
    case "embankment":
      return (P, i) => {
        if (P[i + 4] !== KIND.GROUND) return C.NONE;
        const fill = embankment(P[i + 5]);
        const o = P[i + 6];
        return fill > 0.3 && o > SHOULDER && o < SHOULDER + 2 * fill + 0.6 ? C.LIME : C.NONE;
      };
    case "clear-zone": {
      const [z0, z1] = SITE.clearZone;
      const treeIds = new Set(r.cz.treeIds);
      const poleIds = new Set(r.cz.poleIds);
      return (P, i) => {
        const kind = P[i + 4];
        const s = P[i + 5];
        if (s < z0 || s > z1) return C.NONE;
        if ((kind === KIND.TRUNK || kind === KIND.CANOPY) && treeIds.has(P[i + 7])) return C.RED;
        if (kind === KIND.POLE && poleIds.has(P[i + 7])) return C.RED;
        const o = P[i + 6];
        return kind === KIND.GROUND && o > LANE && o < LANE + r.cz.width ? C.BLUE : C.NONE;
      };
    }
    default:
      return () => C.NONE;
  }
}

/**
 * An analysis's highlight over the whole corridor, worked out once: each point's colour
 * index (`hi`), how far along the spread it sits (`along`, m) and the spread's `speed`.
 */
export function lights(key, world, r) {
  const paint = painter(key, r);
  const sp = spread(key, r);
  const { points: P, count } = world;
  const hi = new Uint8Array(count);
  const along = new Float32Array(count);
  for (let j = 0; j < count; j++) {
    const i = j * STRIDE;
    hi[j] = paint(P, i);
    along[j] = sp ? P[i + 5] - sp.from : 0;
  }
  return { hi, along, speed: sp ? sp.speed : 0 };
}

function inTriangle(t, x, z) {
  const [a, b, c] = t;
  const d1 = (x - b.x) * (a.z - b.z) - (a.x - b.x) * (z - b.z);
  const d2 = (x - c.x) * (b.z - c.z) - (b.x - c.x) * (z - c.z);
  const d3 = (x - a.x) * (c.z - a.z) - (c.x - a.x) * (z - a.z);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

/** Extra geometry the painters and overlays need, derived once from the results. */
export function prepare(world, r) {
  const { al } = world;
  const w = (s, o) => toWorld(al, s, o);
  const s0 = SITE.intersection;
  const eye = w(r.isd.eye.s, r.isd.eye.o);
  const rightEnd = s0 + r.isd.required;
  const rightClear = s0 + r.isd.right.available;
  r.isd.triangles = {
    leftClear: [eye, w(s0, 1.8), w(s0 - r.isd.left.available, 1.8)],
    rightClear: [eye, w(s0, -1.8), w(rightClear, -1.8)],
    rightBlocked: [eye, w(rightClear, -1.8), w(rightEnd, -1.8)],
  };
  r.isd.brushIds = new Set(world.trees.map((t, i) => (t.brush ? i : -1)).filter((i) => i >= 0));
  r.cz.treeIds = r.cz.trees.map((t) => world.trees.indexOf(t));
  r.cz.poleIds = r.cz.poles.map((p) => world.poles.indexOf(p));
  return r;
}

// ---- overlays ------------------------------------------------------------------------

function line(ctx, a, b, color, width = 2, dash = null) {
  if (!a || !b) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  if (dash) ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}
function glowLine(ctx, a, b, i) {
  if (!a || !b) return;
  ctx.save();
  ctx.shadowColor = css(i, 0.8);
  ctx.shadowBlur = 12;
  line(ctx, a, b, css(i, 0.95), 2.2);
  ctx.restore();
}
function dot(ctx, p, i, r = 4) {
  if (!p) return;
  ctx.fillStyle = css(i);
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = css(i, 0.35);
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r + 5, 0, Math.PI * 2);
  ctx.stroke();
}
function ring(ctx, p, i, r = 10) {
  if (!p) return;
  ctx.strokeStyle = css(i, 0.95);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.stroke();
}
function polyline(ctx, pts, color, width = 2, dash = null) {
  const shown = pts.filter(Boolean);
  if (shown.length < 2) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineJoin = "round";
  if (dash) ctx.setLineDash(dash);
  ctx.beginPath();
  shown.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.stroke();
  ctx.restore();
}
/**
 * A label on a dark pill, with a short leader to what it names. The pill stays between
 * `min` and `max` (the frame beside the text); the leader bends to reach it.
 */
function tag(
  ctx,
  p,
  text,
  i = C.WHITE,
  { dy = -28, align = "center", min = 0, max = Infinity } = {}
) {
  if (!p) return;
  ctx.save();
  ctx.font = "600 12px Inter Variable, Segoe UI, system-ui, sans-serif";
  const pad = 7;
  const tw = ctx.measureText(text).width;
  const pw = tw + pad * 2;
  let x = align === "left" ? p.x : align === "right" ? p.x - pw : p.x - pw / 2;
  x = Math.max(min, Math.min(max - pw, x));
  const y = p.y + dy - 11;
  const edge = dy < 0 ? y + 22 : y;
  ctx.strokeStyle = css(i, 0.7);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(p.x, p.y);
  ctx.lineTo(Math.max(x + 8, Math.min(x + pw - 8, p.x)), edge);
  ctx.stroke();
  ctx.fillStyle = "rgba(8,13,19,0.82)";
  ctx.beginPath();
  ctx.roundRect(x, y, pw, 22, 6);
  ctx.fill();
  ctx.strokeStyle = css(i, 0.55);
  ctx.stroke();
  ctx.fillStyle = css(i);
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + pad, y + 11.5);
  ctx.restore();
}

const ftText = (m) => `${Math.round(m * FT).toLocaleString("en-US")} ft`;
/** Metres to feet, to the half foot: AASHTO's 3.5 ft eye and 2 ft object. */
const halfFeet = (m) => Math.round(m * FT * 2) / 2;
const deg = (rad) => `${Math.round((rad * 180) / Math.PI)}°`;

/**
 * Draws an analysis's overlay. `v.at(s, o, above)` and `v.abs(s, o, y)` project a point on
 * the corridor to the screen (null when behind the camera).
 */
export function overlay(key, ctx, v, r) {
  // Labels stay inside the frame beside the step text.
  const label = (p, text, i, o) => tag(ctx, p, text, i, { min: v.left + 8, max: v.w - 8, ...o });
  switch (key) {
    case "ssd": {
      const { eye, required, available, block } = r.ssd;
      const e = v.abs(eye.s, eye.o, eye.y);
      const hit = block ? v.abs(block.s, block.o, block.y) : null;
      const target = v.at(eye.s + required, 1.8, STANDARDS.object);
      glowLine(ctx, e, hit, C.GREEN);
      line(ctx, hit, target, css(C.ORANGE, 0.8), 1.6, [5, 6]);
      dot(ctx, e, C.GREEN);
      dot(ctx, target, C.ORANGE, 3.5);
      label(e, `Driver's eye, ${halfFeet(STANDARDS.eye)} ft up`, C.GREEN, { dy: 32 });
      label(hit, `Crest blocks the view at ${ftText(available)}`, C.ORANGE);
      label(
        target,
        `${halfFeet(STANDARDS.object)} ft object at ${ftText(required)}: hidden`,
        C.ORANGE,
        // Below the road, as the eye's is: on a phone the crest's label fills the line above.
        { dy: 32 }
      );
      break;
    }
    case "psd": {
      const { eye, target, block } = r.psd.example;
      const e = v.abs(eye.s, eye.o, eye.y);
      const hit = block ? v.abs(block.s, block.o, block.y) : null;
      const t = v.abs(target.s, target.o, target.y);
      glowLine(ctx, e, hit, C.GREEN);
      line(ctx, hit, t, css(C.RED, 0.8), 1.6, [5, 6]);
      dot(ctx, e, C.GREEN);
      if (r.psd.shortFrom !== null) {
        label(
          v.at(r.psd.shortFrom, 0, 0),
          `Striped for passing, ${ftText(r.psd.shortLength)} short`,
          C.RED
        );
      }
      label(v.at(SITE.passing[0] + 40, 0, 0), "Passing zone", C.GREEN, { dy: -24 });
      break;
    }
    case "isd": {
      const t = r.isd.triangles;
      const toScreen = (p) => v.world(p.x, heightAtWorld(v, p), p.z);
      for (const [tri, i, alpha] of [
        [t.leftClear, C.GREEN, 0.16],
        [t.rightClear, C.GREEN, 0.16],
        [t.rightBlocked, C.ORANGE, 0.2],
      ]) {
        const pts = tri.map(toScreen);
        if (pts.some((p) => !p)) continue;
        ctx.fillStyle = css(i, alpha);
        ctx.beginPath();
        pts.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        ctx.fill();
        polyline(ctx, [...pts, pts[0]], css(i, 0.85), 1.5);
      }
      const e = v.abs(r.isd.eye.s, r.isd.eye.o, r.isd.eye.y);
      dot(ctx, e, C.GREEN);
      label(
        v.at(SITE.intersection - r.isd.left.available, 1.8, 0),
        `Clear to ${ftText(r.isd.left.available)}`,
        C.GREEN
      );
      const hit = r.isd.right.block;
      if (hit)
        label(
          v.abs(hit.s, hit.o, hit.y),
          `Brush blocks at ${ftText(r.isd.right.available)}`,
          C.RED
        );
      label(e, "Stop line", C.WHITE, { dy: 30 });
      break;
    }
    case "signs": {
      for (const sign of r.tsa.signs) {
        const corners = [
          [-1, -1],
          [1, -1],
          [1, 1],
          [-1, 1],
        ].map(([u, vv]) => {
          const y = sign.base + sign.cy + (vv * sign.h) / 2;
          return sign.face === "side"
            ? v.abs(sign.s + (u * sign.w) / 2, sign.o, y)
            : v.abs(sign.s, sign.o + (u * sign.w) / 2, y);
        });
        if (corners.some((p) => !p)) continue;
        const xs = corners.map((p) => p.x);
        const ys = corners.map((p) => p.y);
        const box = {
          x: Math.min(...xs) - 4,
          y: Math.min(...ys) - 4,
          w: Math.max(...xs) - Math.min(...xs) + 8,
          h: Math.max(...ys) - Math.min(...ys) + 8,
        };
        if (box.w < 6 || box.x > v.w || box.x + box.w < 0) continue;
        ctx.strokeStyle = css(C.PINK, 0.95);
        ctx.lineWidth = 1.5;
        ctx.strokeRect(box.x, box.y, box.w, box.h);
        if (sign.code !== "W13-1P")
          label({ x: box.x + box.w / 2, y: box.y }, sign.code, C.PINK, { dy: -14 });
      }
      break;
    }
    case "flat-spots": {
      const run = r.fsa.runs[0];
      if (run) label(v.at((run.from + run.to) / 2, 1.8, 0), "Cross slope 0%: water stays", C.CYAN);
      label(v.at(528, -1.8, 0), "2% crown", C.WHITE, { dy: -22 });
      label(v.at(575, 1.8, 0), "6% superelevation", C.BLUE, { dy: -22 });
      break;
    }
    case "curves": {
      const { radius } = SITE.curve;
      const [a] = SITE.curve.arc;
      const [, b] = SITE.curve.arc;
      const mid = (a + b) / 2;
      const center = v.abs(mid, -radius, v.profile(mid));
      const ends = [a, b].map((s) => v.at(s, 0, 0));
      for (const end of ends) line(ctx, center, end, css(C.PURPLE, 0.9), 1.6, [6, 6]);
      dot(ctx, center, C.PURPLE, 3.5);
      const arc = [];
      for (let s = a; s <= b; s += 4) arc.push(v.at(s, 0, 0));
      polyline(ctx, arc, css(C.PURPLE, 0.95), 2.4);
      label(center, `R = ${ftText(radius)}`, C.PURPLE, { dy: 30 });
      label(v.at(mid, 0, 0), `Safe speed ${Math.round(r.hca.safeMph)} mph, posted 55`, C.RED);
      break;
    }
    case "glare": {
      const sun = v.dir(r.glr.azimuth, r.glr.elevation);
      if (sun) {
        const g = ctx.createRadialGradient(
          sun.x,
          sun.y,
          0,
          sun.x,
          sun.y,
          Math.max(v.w, v.h) * 0.45
        );
        g.addColorStop(0, "rgba(255,244,214,0.95)");
        g.addColorStop(0.05, "rgba(255,226,150,0.6)");
        g.addColorStop(0.25, "rgba(250,204,21,0.16)");
        g.addColorStop(1, "rgba(250,204,21,0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, v.w, v.h);
        label(
          { x: sun.x, y: sun.y + 16 },
          `Sun ${deg(r.glr.elevation)} above the horizon, ${deg(r.glr.offset)} off the lane`,
          C.YELLOW,
          { dy: 40 }
        );
      }
      break;
    }
    case "guardrail": {
      const [r0, r1] = SITE.guardrail;
      label(v.at((r0 + r1) / 2, 6.3, 0.9), "Guardrail", C.WHITE);
      const { gap } = r.grw;
      if (!gap) break;
      label(v.at((gap.from + gap.to) / 2, 6.3, 0.2), "Warranted, no barrier", C.RED);
      // The fill measured where the open stretch is deepest: the height the readout gives.
      const fill = embankment(gap.deepest);
      const top = v.at(gap.deepest, SHOULDER, 0);
      const toe = v.at(gap.deepest, SHOULDER + 2 * fill, 0);
      line(ctx, top, toe, css(C.RED, 0.9), 1.6, [4, 4]);
      label(toe, `${Math.round(fill * FT)} ft fill, 1V:2H`, C.RED, { dy: 30 });
      break;
    }
    case "embankment": {
      for (const section of r.emb.sections) {
        // Sections that fall behind the step text would cross it; the rest show the spacing.
        const shoulder = v.at(section.s, SHOULDER, 0);
        if (!shoulder || shoulder.x < v.left) continue;
        polyline(
          ctx,
          section.line.map((pt) => v.abs(section.s, pt.o, pt.y + 0.05)),
          css(C.LIME, 0.95),
          1.8
        );
      }
      const last = r.emb.sections[Math.floor(r.emb.sections.length / 2)];
      label(
        v.abs(last.s, 24, heightAt(v.al, last.s, 24)),
        `Cross-section every ${Math.round(r.emb.everyFt)} ft`,
        C.LIME,
        { dy: -60 }
      );
      break;
    }
    case "clear-zone": {
      const [z0, z1] = SITE.clearZone;
      const edge = [];
      for (let s = z0; s <= z1; s += 4) edge.push(v.at(s, LANE + r.cz.width, 0.05));
      polyline(ctx, edge, css(C.BLUE, 0.95), 1.8, [7, 6]);
      for (const t of r.cz.trees) ring(ctx, v.at(t.s, t.o, 1.2), C.RED, 9);
      for (const p of r.cz.poles) ring(ctx, v.at(p.s, p.o, 1.2), C.RED, 9);
      label(
        v.at(z0 + 30, LANE + r.cz.width, 0),
        `Clear zone ${Math.round(r.cz.width * FT)} ft`,
        C.BLUE,
        { dy: -24 }
      );
      break;
    }
    default:
  }
}

function heightAtWorld(v, p) {
  const { s, o } = v.locate(p.x, p.z);
  return heightAt(v.al, s, o) + 0.05;
}

// ---- readouts ------------------------------------------------------------------------

const byKey = Object.fromEntries(ANALYSES.map((a) => [a.key, a]));
/** "W1-8, W1-8, W1-8" -> "W1-8 ×3", in first-seen order. */
function grouped(codes) {
  const counts = new Map();
  for (const c of codes) counts.set(c, (counts.get(c) || 0) + 1);
  return [...counts].map(([c, n]) => (n > 1 ? `${c} ×${n}` : c)).join(", ");
}

/**
 * What the readout shows for an analysis: rows, a verdict, and `summary`, the one line a
 * phone shows in place of the rows.
 */
export function readout(key, r) {
  const a = byKey[key];
  const head = { code: a.code, title: a.title, tint: a.tint };
  const ft = (m) => Math.round(m * FT);
  switch (key) {
    case "ssd":
      return {
        ...head,
        rows: [
          ["Available", ftText(r.ssd.available)],
          ["Required", `${STANDARDS.ssdFt} ft`],
          ["Blocked by", r.ssd.block ? r.ssd.block.by : "—"],
        ],
        summary: `${ft(r.ssd.available)} of ${STANDARDS.ssdFt} ft`,
        status: r.ssd.deficient ? ["Deficient", "fail"] : ["Meets AASHTO", "ok"],
      };
    case "psd":
      return {
        ...head,
        rows: [
          ["Passing zone", ftText(r.psd.zone)],
          ["Sight needed", `${STANDARDS.psdFt} ft`],
          ["Striped without it", ftText(r.psd.shortLength)],
        ],
        summary: `${ft(r.psd.shortLength)} ft without sight`,
        status: r.psd.shortLength ? ["Zone too long", "fail"] : ["Meets MUTCD", "ok"],
      };
    case "isd":
      return {
        ...head,
        rows: [
          ["Left turn needs", `${STANDARDS.isdFt} ft`],
          ["Looking left", ftText(r.isd.left.available)],
          ["Looking right", ftText(r.isd.right.available)],
          ["Blocked by", r.isd.right.block ? r.isd.right.block.by : "—"],
        ],
        summary: `Right: ${ft(r.isd.right.available)} of ${STANDARDS.isdFt} ft`,
        status: r.isd.right.block ? ["Deficient", "fail"] : ["Meets AASHTO", "ok"],
      };
    case "signs": {
      const codes = r.tsa.signs.map((s) => s.code);
      return {
        ...head,
        rows: [
          ["Signs found", String(codes.length)],
          ["Regulatory", codes.filter((c) => c.startsWith("R")).join(", ")],
          ["Warning", grouped(codes.filter((c) => c.startsWith("W")))],
        ],
        summary: `${codes.length} signs, with MUTCD codes`,
        status: ["Inventoried", "info"],
      };
    }
    case "flat-spots": {
      const run = r.fsa.runs[0];
      return {
        ...head,
        rows: [
          ["Cross slope", "under 0.5%"],
          ["Grade", run ? `${Math.abs(run.grade * 100).toFixed(1)}%` : "—"],
          ["Flat spots", `${r.fsa.runs.length}, ${run ? ftText(run.length) : "—"} each`],
        ],
        summary: `${r.fsa.runs.length} flat spots`,
        status: r.fsa.runs.length ? ["Ponding risk", "warn"] : ["Drains", "ok"],
      };
    }
    case "curves":
      return {
        ...head,
        rows: [
          ["Radius", ftText(SITE.curve.radius)],
          ["Superelevation", `${(r.hca.e * 100).toFixed(1)}%`],
          ["Safe speed", `${Math.round(r.hca.safeMph)} mph`],
          ["Posted", `${r.hca.postedMph} mph`],
        ],
        summary: `Safe at ${Math.round(r.hca.safeMph)} mph`,
        status: r.hca.below ? ["Below posted speed", "fail"] : ["Meets AASHTO", "ok"],
      };
    case "glare":
      return {
        ...head,
        rows: [
          ["Sun elevation", deg(r.glr.elevation)],
          ["Off the lane", deg(r.glr.offset)],
          ["Glare stretch", ftText(r.glr.length)],
        ],
        summary: `Sun ${deg(r.glr.offset)} off the lane`,
        status: ["Glare risk", "warn"],
      };
    case "guardrail":
      return {
        ...head,
        rows: [
          ["Fill height", `${Math.round(r.grw.heightFt)} ft`],
          ["Slope", "1V:2H"],
          ["Guardrail", `${Math.round(r.grw.railFt)} ft`],
          ["Unprotected", `${Math.round(r.grw.unprotectedFt)} ft`],
        ],
        summary: `${Math.round(r.grw.unprotectedFt)} ft unprotected`,
        status: r.grw.unprotectedFt > 0 ? ["Barrier warranted", "fail"] : ["Protected", "ok"],
      };
    case "embankment":
      return {
        ...head,
        rows: [
          ["Cross-sections", String(r.emb.sections.length)],
          ["Every", `${Math.round(r.emb.everyFt)} ft`],
          ["Side slope", "1V:2H"],
        ],
        summary: `${r.emb.sections.length} sections, ${Math.round(r.emb.everyFt)} ft apart`,
        status: ["DXF ready", "ok"],
      };
    case "clear-zone":
      return {
        ...head,
        rows: [
          ["Clear zone", `${Math.round(r.cz.width * FT)} ft`],
          ["Trees inside", String(r.cz.trees.length)],
          ["Poles inside", String(r.cz.poles.length)],
        ],
        summary: `${Math.round(r.cz.width * FT)} ft clear zone`,
        status: r.cz.objects ? [`${r.cz.objects} fixed objects`, "fail"] : ["Clear", "ok"],
      };
    default:
      return { ...head, rows: [], summary: "", status: ["", "info"] };
  }
}
